/* ============================================================================================
   logo-ink-hull.js — THE OUTLINE OF A LOGO'S INK, so the size dial can stop where the INK meets
   the edge rather than where the IMAGE BOX does.
   CUSTOM_WEIGHTDESIGNER_BY_CLAUDE (Ryan 2026-09-26)

   Ryan, on RW-1444-A's rams head: *"I've got it at 100% right now … and it's still so far from the
   edges."* He was right. The engine bounds a logo by its axis-aligned image box, and a mark that
   has drifted round the ring (that one sits at ~229°) points a box CORNER straight at the rim —
   a corner the round rams head never fills. Measured: the box hit the ring at ~105%, the ink not
   until ~165%. His rule: grow "until it touches the text or touches the edge, never overlap".

   So this measures the ink ONCE per artwork and hands the engine a convex hull in the image box's
   own normalised coordinates (u right, v down, both in [-0.5, 0.5]). A convex hull is a SUPERSET of
   the ink, which is the safe direction for both tests the engine runs with it: the farthest ink
   from the plate centre is always a hull vertex (distance is convex), and the nearest point of the
   hull is never further out than the nearest ink. So a hull that fits guarantees the ink fits.

   ⚠ TRANSIENT, like `renderUrl`. It is never serialised: it is re-measured from the artwork on
   every load, keyed on the same `length:tail` fingerprint `syncLogoRaster` keys its composite on.
   ⚠ MEASURED ON THE ORIGINAL ARTWORK. A peel or a recolour moves no ink outward, so the hull stays
   a superset. An ADDED OUTLINE RING does grow the ink and pads the image (it sets `renderAspect`);
   the engine refuses the hull whenever the drawn aspect differs from the one it was measured at, and
   falls back to the box — conservative, never out of bounds.
   ============================================================================================ */

const SIZE = 256;          // long side of the measuring raster
const ALPHA_MIN = 32;      // a pixel below this is not ink (anti-alias fringe)

/** The fingerprint `syncLogoRaster` keys on — length plus the tail of the data URL. */
export function inkHullKey(dataUrl) {
  return typeof dataUrl === 'string' && dataUrl ? `${dataUrl.length}:${dataUrl.slice(-16)}` : '';
}

/** Andrew's monotone chain. Points are [x, y]; returns the hull counter-clockwise, no repeats. */
export function convexHull(points) {
  const pts = points.slice().sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
  if (pts.length < 3) return pts;
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

/**
 * Measure the ink hull of an artwork, drawn STRETCHED to `aspect` exactly as the renderer draws it
 * (`preserveAspectRatio="none"` into a box of that aspect).
 * @returns {Promise<{key:string, aspect:number, pts:number[][]}|null>} null when it cannot be read
 */
export function measureInkHull(dataUrl, aspect) {
  if (typeof document === 'undefined' || !dataUrl || !(aspect > 0)) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const W = aspect >= 1 ? SIZE : Math.max(8, Math.round(SIZE * aspect));
        const H = aspect >= 1 ? Math.max(8, Math.round(SIZE / aspect)) : SIZE;
        const cv = document.createElement('canvas');
        cv.width = W; cv.height = H;
        const g = cv.getContext('2d', { willReadFrequently: true });
        g.drawImage(img, 0, 0, W, H);
        const a = g.getImageData(0, 0, W, H).data;
        /* Only each row's outermost ink matters to a convex hull, plus each pixel's four corners so
           the hull covers whole pixels rather than their centres. */
        const pts = [];
        for (let y = 0; y < H; y++) {
          let lo = -1, hi = -1;
          for (let x = 0; x < W; x++) {
            if (a[(y * W + x) * 4 + 3] >= ALPHA_MIN) { if (lo < 0) lo = x; hi = x; }
          }
          if (lo < 0) continue;
          pts.push([lo, y], [lo, y + 1], [hi + 1, y], [hi + 1, y + 1]);
        }
        if (pts.length < 3) { resolve(null); return; }
        const hull = convexHull(pts).map(([x, y]) => [
          Math.round((x / W - 0.5) * 1e4) / 1e4,
          Math.round((y / H - 0.5) * 1e4) / 1e4,
        ]);
        resolve({ key: inkHullKey(dataUrl), aspect, pts: hull });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}
